import React, { useCallback, useState, useMemo, useEffect } from 'react';
import _get from 'lodash/get';
import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { translator } from 'mage-i18n';
import { IconSearch, SpacerVertical } from '@klarna/bubble-ui';
import { AutoSuggest } from '@klarna/mp-ui';
import { analytics, defaultContainerPropTypes, HorizontalWrapper } from 'moet-core';
import { generateCyElemId } from 'moet-test-utils/cypress/utils';

import { CAPS } from '@/constants';
import * as a from '@/actions/signup';
import { withCapabilityBeforeNext } from '@/flow/common';
import { getSelectedPlatform, getPlatforms } from '@/selectors/platforms';
import { getSelectedCountryKey, hasAboveThresholdVolume } from '@/selectors/data';
import { merchantRegion } from '@/selectors/merchant';
import { ContainerTemplate, withLeadUpdateOnNext } from '@/components';
import BackButton from '@/components/BackButton';

import { ANALYTICS_EVENTS } from './analyticsEvents';
import { CY_PLATFORM_CONTAINER_IDS, T_PREFIX } from './constants';
import { getPlatformsForDropdown } from './utils';

export const PlatformOther = ({
  country,
  error,
  isDisabled,
  isEnterprise,
  onBack,
  onChangePlatform,
  onNext,
  platform,
  platforms,
  region,
  t,
}) => {
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  const filteredPlatforms = platforms.filter(
    ({ name, id }) =>
      (id === 'custom' && !value) || // Custom needs to appear when nothing is typed
      !value ||
      name.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) >= 0,
  );

  const options = (!isEnterprise || filteredPlatforms.length > 0
    ? getPlatformsForDropdown(filteredPlatforms, country, region)
    : [platforms.find(({ id }) => id === 'custom'), { id: value, name: value }]
  ).map(({ id, name }) => ({ value: id, label: name }));

  const onSelect = useCallback(
    platform => {
      if (loading) {
        return;
      }
      setLoading(true);
      onNext({
        payload: { params: { country, platform } },
        leadData: { platform },
        onNextCallback: () => onChangePlatform(platform),
      }).catch(() => setLoading(false));
    },
    [loading],
  );

  const handleChange = value => {
    if (value.length >= 4) {
      analytics.logEvent(ANALYTICS_EVENTS.searchedForPlatform, { platformSearched: value });
    }
    setValue(value);
  };

  const debouncedResults = useMemo(() => {
    return debounce(handleChange, 500);
  }, []);

  return (
    <ContainerTemplate
      title={t(`${T_PREFIX}.title_line_1`)}
      titleLine2={t(`${T_PREFIX}.title_line_2`)}
      subtitle={t(`${T_PREFIX}.subtitle`)}
      showRecaptchaFooter
    >
      <SpacerVertical large />
      <AutoSuggest
        borderless
        disabled={isDisabled || loading}
        emptyOverlayMessage={t(`${T_PREFIX}.platform_search.no_match`, { input: value })}
        icon={<IconSearch />}
        label={t(`${T_PREFIX}.platform_search.label`)}
        value={_get(platform, 'name')}
        onChange={debouncedResults}
        onSelect={onSelect}
        showOptionsOnEmptyInput
        showOptionsOnFocus
        options={options}
        hasError={error}
        {...generateCyElemId(CY_PLATFORM_CONTAINER_IDS.PLATFORM_OTHER_INPUT, window)}
      />
      <SpacerVertical large />
      <HorizontalWrapper display="inline-flex" alignItems="center" justifyContent="flex-start">
        <BackButton
          onClick={onBack}
          disabled={isDisabled}
          loading={loading}
          {...generateCyElemId(CY_PLATFORM_CONTAINER_IDS.BTN_BACK, window)}
        />
      </HorizontalWrapper>
    </ContainerTemplate>
  );
};

PlatformOther.propTypes = {
  ...defaultContainerPropTypes,
  platforms: PropTypes.array.isRequired,
  region: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
  onChangePlatform: PropTypes.func.isRequired,
};

export default compose(
  connect(
    state => ({
      country: getSelectedCountryKey(state),
      isEnterprise: hasAboveThresholdVolume(state),
      platform: getSelectedPlatform(state),
      platforms: getPlatforms(state),
      region: merchantRegion(state),
      t: translator(state),
    }),
    dispatch => ({
      onChangePlatform: platform =>
        new Promise(resolve => {
          a.onChangeFormValue('platform', platform)(dispatch);
          resolve();
        }),
    }),
  ),
  withCapabilityBeforeNext({ cap: CAPS.PRODUCTS_GET }, ({ payload }) => {
    const { platform } = payload.params;
    analytics.logEvent(ANALYTICS_EVENTS.platformSelected, { platform });
  }),
  withLeadUpdateOnNext,
)(PlatformOther);
